import React, { useMemo } from 'react';
import { solarElevation, phase, sunEvents } from '../lib/sun';
import {
  describeTransition, formatOffset, hhmm, localParts, nextTransition, offsetMinutes, summerTime, zoneAbbrev,
} from '../lib/time';

const HOUR = 3600e3;
const DAY = 24 * HOUR;
const SAMPLES = 96; // shading resolution: every 15 minutes
const PIN_COLORS = { blue: '#2563eb', green: '#16a34a', amber: '#d97706', red: '#dc2626', violet: '#7c3aed', slate: '#475569' };
export { PIN_COLORS };

const SHADE = { day: 'var(--day)', twilight: 'var(--twilight)', night: 'var(--night)' };

/** CSS gradient of day / twilight / night across the 24 h window for one place. */
function shading(city, start) {
  if (city.lat == null) return null;
  const stops = [];
  let prev = null;
  for (let i = 0; i <= SAMPLES; i++) {
    const ph = phase(solarElevation(start + (i / SAMPLES) * DAY, city.lat, city.lng));
    const pct = ((i / SAMPLES) * 100).toFixed(2);
    if (ph !== prev) {
      if (prev) stops.push(`${SHADE[prev]} ${pct}%`);
      stops.push(`${SHADE[ph]} ${pct}%`);
      prev = ph;
    }
  }
  stops.push(`${SHADE[prev]} 100%`);
  return `linear-gradient(to right, ${stops.join(', ')})`;
}

function Row({ city, isHome, start, now, pins, hover, onHover, onPick, onEditPin, onRemove, onHome, onMove, first, last }) {
  const mid = start + DAY / 2;
  const off = offsetMinutes(city.timezone, mid);
  const abbrev = zoneAbbrev(city.timezone, mid);
  const summer = summerTime(city.timezone, mid);
  const change = useMemo(() => nextTransition(city.timezone, start, 14), [city.timezone, start]);
  const bg = useMemo(() => shading(city, start), [city, start]);
  const sun = useMemo(() => (city.lat == null ? null : sunEvents(city.lat, city.lng, start, start + DAY)), [city, start]);
  const nowLocal = localParts(city.timezone, now);

  const cells = [];
  for (let k = 0; k < 24; k++) {
    const t = start + k * HOUR;
    const p = localParts(city.timezone, t);
    const elev = city.lat == null ? 10 : solarElevation(t + HOUR / 2, city.lat, city.lng);
    cells.push({
      k, label: String(p.h), minutes: p.mi ? `:${String(p.mi).padStart(2, '0')}` : null,
      newDay: p.h === 0 && p.mi === 0 ? p.wd : null,
      dark: phase(elev) !== 'day',
      work: p.h >= 9 && p.h < 17,
    });
  }

  const frac = (t) => (t - start) / DAY;
  const pointer = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    return Math.min(0.9999, Math.max(0, (e.clientX - r.left) / r.width));
  };
  const nowFrac = frac(now);
  const hoverLocal = hover != null ? localParts(city.timezone, start + hover * DAY) : null;

  return (
    <div className={`row${isHome ? ' home' : ''}`}>
      <div className="meta">
        <div className="meta-top">
          <h2 className="city">{city.name}</h2>
          {isHome && <span className="tag">Home</span>}
        </div>
        <div className="clock">
          <span className="clock-time">{hhmm(nowLocal)}</span>
          <span className="clock-day">{nowLocal.wd}</span>
        </div>
        <div className="zone">
          {abbrev ? `${abbrev} · ` : ''}{formatOffset(off)}
          {summer === true && <span className="season summer" title="Daylight saving time">Summer time</span>}
          {summer === false && <span className="season" title="Standard time">Winter time</span>}
        </div>
        {change && <div className="dst">{describeTransition(city.timezone, change)}</div>}
        {sun && (sun.sunrise || sun.sunset) && (
          <div className="sun">
            {sun.sunrise && <>Sunrise {hhmm(localParts(city.timezone, sun.sunrise))}</>}
            {sun.sunrise && sun.sunset && ' · '}
            {sun.sunset && <>Sunset {hhmm(localParts(city.timezone, sun.sunset))}</>}
          </div>
        )}
        <div className="row-actions">
          {!isHome && <button onClick={() => onHome(city.name)}>Make home</button>}
          <button onClick={() => onMove(city.name, -1)} disabled={first} aria-label={`Move ${city.name} up`}>↑</button>
          <button onClick={() => onMove(city.name, 1)} disabled={last} aria-label={`Move ${city.name} down`}>↓</button>
          <button onClick={() => onRemove(city.name)} aria-label={`Remove ${city.name}`}>Remove</button>
        </div>
      </div>

      <div
        className="strip"
        style={bg ? { backgroundImage: bg } : undefined}
        onMouseMove={(e) => onHover(pointer(e))}
        onMouseLeave={() => onHover(null)}
        onClick={(e) => onPick(Math.round((start + pointer(e) * DAY) / (15 * 60e3)) * 15 * 60e3)}
        role="presentation"
      >
        {cells.map((c) => (
          <div key={c.k} className={`cell${c.dark ? ' dark' : ''}${c.work ? ' work' : ''}${c.newDay ? ' newday' : ''}`}>
            {c.newDay && <span className="newday-label">{c.newDay}</span>}
            <span className="hour">{c.label}{c.minutes && <span className="min">{c.minutes}</span>}</span>
          </div>
        ))}
        {nowFrac >= 0 && nowFrac < 1 && <div className="now" style={{ left: `${nowFrac * 100}%` }} />}
        {pins.map((p) => {
          const f = frac(p.t);
          if (f < 0 || f >= 1) return null;
          return (
            <button
              key={p.id}
              className="pin"
              style={{ left: `${f * 100}%`, '--pin': PIN_COLORS[p.color] || PIN_COLORS.blue }}
              onClick={(e) => { e.stopPropagation(); onEditPin(p); }}
              title={`${p.label} · ${hhmm(localParts(city.timezone, p.t))} in ${city.name}`}
            >
              <span className="pin-time">{hhmm(localParts(city.timezone, p.t))}</span>
              {isHome && <span className="pin-label">{p.label}</span>}
            </button>
          );
        })}
        {hover != null && (
          <div className="scrub" style={{ left: `${hover * 100}%` }}>
            <span className="scrub-time">{hhmm(hoverLocal)}{hoverLocal.date !== localParts(city.timezone, start + DAY / 2).date ? ` ${hoverLocal.wd}` : ''}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Board(props) {
  const { cities } = props;
  return (
    <div className="board" onMouseLeave={() => props.onHover(null)}>
      {cities.map((city, i) => (
        <Row key={city.name} city={city} isHome={i === 0} first={i === 0} last={i === cities.length - 1} {...props} />
      ))}
    </div>
  );
}
